<template>
  <dialog
    ref="toaster"
    class="toast fixed z-1010 flex w-fit justify-center bg-transparent focus:outline-none"
    role="alert"
    :class="{
      'right-2 bottom-10': position === 'bottom-right',
      'bottom-10 left-2': position === 'bottom-left',
      'top-10 right-2': position === 'top-right',
      'top-10 left-2': position === 'top-left',
      'top-10 left-1/2 -translate-x-1/2 transform': position === 'top-center',
      'bottom-10 left-1/2 -translate-x-1/2 transform': position === 'bottom-center',
    }"
  >
    <transition-group class="flex flex-col items-center gap-2" name="toaster-toast" tag="div">
      <ToasterToast
        v-for="message in messagesWithoutTarget"
        :key="message.id"
        :message="message"
        @dismiss="remove($event, true)"
      />
    </transition-group>

    <Teleport v-for="[target, g] in Object.entries(groupedMessagesWithTarget)" :key="target" :to="target">
      <ToasterToast v-for="message in g" :key="message.id" :message="message" @dismiss="remove($event, true)" />
    </Teleport>
  </dialog>
</template>

<script setup lang="ts">
  import type { IToast } from "~/types/toast";

  const {
    position = "bottom-center",
    duration = 5000,
    groups = true,
    reverse = false,
  } = defineProps<{
    position?: "top-left" | "top-right" | "bottom-left" | "bottom-right" | "top-center" | "bottom-center";
    duration?: number;
    groups?: boolean;
    reverse?: boolean;
  }>();

  const emit = defineEmits(["added", "dismissed", "removed", "cleared"]);

  const toaster = useTemplateRef("toaster");
  const messages = ref<IToast[]>([]);

  const messagesWithTarget = computed(() => messages.value.filter((message) => message.target));
  const messagesWithoutTarget = computed(() => messages.value.filter((message) => !message.target));

  const groupedMessagesWithTarget = computed<Record<string, IToast[]>>(() => {
    const groups: Record<string, IToast[]> = {};
    messagesWithTarget.value.forEach((message) => {
      if (!groups[message.target]) groups[message.target] = [];
      groups[message.target].push(message);
    });
    return groups;
  });

  function open() {
    if (toaster.value === null) return;

    //We manually set open instead of using show() to prevet focus grabbing
    toaster.value.open = true;
  }

  function close() {
    if (!toaster.value) return;

    //check if close is available and a function before calling it
    if (toaster.value.close && typeof toaster.value.close === "function") {
      toaster.value.close();
    } else {
      toaster.value.open = false;
    }
  }

  const hashCode = (s) => Math.abs(s.split("").reduce((a, b) => ((a << 5) - a + b.charCodeAt(0)) | 0, 0));

  let messageId = 1;

  onMounted(() => {
    EventBus.$on("TOAST_ADD", (ev) => {
      emit("added", ev);
      if (!ev.group)
        ev.group = hashCode(`${ev.color}${ev.title}${ev.text}${getDateRoundedToNearestNthSeconds(5)}`).toString(16);
      // If there's a default duration and no message duration is set, use the default
      if (duration && !ev.duration && ev.duration !== 0) ev.duration = duration;
      if (position && !ev.position && ev.position !== 0) ev.position = position;

      if (ev.id) {
        remove(ev);
      }

      // Find the existing message if one with the same group-key already exists
      const existingGroup = ev.group && messages.value.find((msg) => msg.group === ev.group);

      if (groups === false || !existingGroup) {
        const message = {
          ...ev,
          id: ev.id || messageId,
          count: 1,
        };
        if (reverse) messages.value.unshift(message);
        else messages.value.push(message);

        open();
        if (!ev.id) messageId++;
      } else {
        existingGroup.count++;
      }
    });

    EventBus.$on("TOAST_CLEAR", () => {
      emit("cleared");
      messages.value = [];
      close();
    });

    EventBus.$on("TOAST_REMOVE", (messageId: string | number) => {
      remove({ id: messageId });
    });
  });

  const getDateRoundedToNearestNthSeconds = (n) => {
    const d = new Date();
    const seconds = Math.floor(d.getSeconds() / n) * n;
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes(), seconds, 0);
  };

  onUnmounted(() => {
    EventBus.$off("TOAST_ADD");
    EventBus.$off("TOAST_CLEAR");
    close();
  });

  const remove = (ev: { id: string | number }, wasDismissed = false) => {
    if (!ev?.id) return;

    if (wasDismissed) emit("dismissed", ev);
    else emit("removed", ev);
    messages.value = messages.value.filter((message) => {
      return message.id !== ev.id;
    });
  };

  watch(messages, (newMessages) => {
    if (newMessages.length > 0) open();
    else close();
  });
</script>
